import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Save, Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";
import {
  fetchImpactStats,
  fetchProjects,
  adminListApplications,
  adminUpdateStats,
  adminUpsertProject,
  adminDeleteProject,
  adminLogout,
} from "@/lib/api";

const emptyProject = { id: null, business_name: "", category: "", description: "", image_url: "", location: "", live_url: "", featured: true };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ websites_built: 0, businesses_helped: 0, funds_raised_gbp: 0, sponsors_count: 0 });
  const [projects, setProjects] = useState([]);
  const [apps, setApps] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, p, a] = await Promise.all([fetchImpactStats(), fetchProjects(), adminListApplications()]);
      setStats(s);
      setProjects(p);
      setApps(a);
    } catch (err) {
      toast.error("Session expired. Please sign in again.");
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("dh_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const onLogout = () => {
    adminLogout();
    navigate("/admin/login");
  };

  const saveStats = async () => {
    try {
      await adminUpdateStats({
        websites_built: Number(stats.websites_built) || 0,
        businesses_helped: Number(stats.businesses_helped) || 0,
        funds_raised_gbp: Number(stats.funds_raised_gbp) || 0,
        sponsors_count: Number(stats.sponsors_count) || 0,
      });
      toast.success("Impact stats updated.");
    } catch (err) {
      toast.error("Couldn't save stats.");
    }
  };

  const saveProject = async () => {
    if (!editing.business_name || !editing.image_url) {
      toast.error("Name and image are required.");
      return;
    }
    try {
      const payload = { ...editing };
      if (!payload.id) delete payload.id;
      await adminUpsertProject(payload);
      toast.success("Project saved.");
      setEditing(null);
      const p = await fetchProjects();
      setProjects(p);
    } catch (err) {
      toast.error("Couldn't save project.");
    }
  };

  const removeProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await adminDeleteProject(id);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch (err) {
      toast.error("Couldn't delete.");
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-950 overflow-hidden">
              <img src="/brand/dh-logo.png" alt="DH" className="w-8 h-8 object-contain" />
            </span>
            <span className="font-display font-semibold text-slate-900 text-[15px]">Admin Dashboard</span>
          </Link>
          <button onClick={onLogout} className="inline-flex items-center gap-2 text-[14px] text-slate-600 hover:text-slate-900" data-testid="admin-logout-btn">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12 space-y-12">
        {/* Stats */}
        <section className="bg-white border border-slate-100 rounded-3xl p-8" data-testid="admin-stats-section">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="eyebrow text-blue-600">Impact stats</p>
              <h2 className="mt-2 font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Public numbers shown on the site</h2>
            </div>
            <button onClick={saveStats} className="btn-primary" data-testid="admin-save-stats-btn">
              <Save size={16} /> Save changes
            </button>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { key: "websites_built", label: "Websites built" },
              { key: "businesses_helped", label: "Businesses helped" },
              { key: "funds_raised_gbp", label: "Funds raised (£)" },
              { key: "sponsors_count", label: "Sponsors" },
            ].map((f) => (
              <label key={f.key} className="block">
                <span className="text-[12px] uppercase tracking-[0.18em] font-semibold text-slate-500">{f.label}</span>
                <input
                  type="number"
                  step="any"
                  value={stats[f.key]}
                  onChange={(e) => setStats((s) => ({ ...s, [f.key]: e.target.value }))}
                  className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-lg font-display focus:outline-none focus:border-blue-600 focus:bg-white"
                  data-testid={`admin-stat-${f.key}`}
                />
              </label>
            ))}
          </div>
        </section>

        {/* Projects */}
        <section className="bg-white border border-slate-100 rounded-3xl p-8" data-testid="admin-projects-section">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="eyebrow text-blue-600">Projects showcase</p>
              <h2 className="mt-2 font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Featured builds on the home page</h2>
            </div>
            <button onClick={() => setEditing({ ...emptyProject })} className="btn-primary" data-testid="admin-new-project-btn">
              <Plus size={16} /> Add project
            </button>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p) => (
              <div key={p.id} className="bg-slate-50 rounded-2xl border border-slate-100 overflow-hidden" data-testid={`admin-project-${p.id}`}>
                <div className="aspect-[16/10] bg-slate-200 overflow-hidden">
                  <img src={p.image_url} alt={p.business_name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <p className="text-[12px] uppercase tracking-[0.18em] font-semibold text-blue-600">{p.category}</p>
                  <p className="mt-2 font-display text-lg font-medium text-slate-900">{p.business_name}</p>
                  <p className="mt-2 text-[13px] text-slate-600 line-clamp-2">{p.description}</p>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => setEditing({ ...p })} className="text-[13px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:bg-white">
                      <Edit3 size={13} /> Edit
                    </button>
                    <button onClick={() => removeProject(p.id)} className="text-[13px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50">
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Applications */}
        <section className="bg-white border border-slate-100 rounded-3xl p-8" data-testid="admin-applications-section">
          <p className="eyebrow text-blue-600">Applications</p>
          <h2 className="mt-2 font-display text-2xl md:text-3xl font-medium tracking-tight text-slate-900">Latest applicants ({apps.length})</h2>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-left text-[14px]">
              <thead>
                <tr className="border-b border-slate-200 text-[12px] uppercase tracking-[0.15em] text-slate-500">
                  <th className="py-3 pr-4">Name</th>
                  <th className="py-3 pr-4">Business</th>
                  <th className="py-3 pr-4">Email</th>
                  <th className="py-3 pr-4">Situation</th>
                  <th className="py-3 pr-4">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {apps.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-10 text-center text-slate-500">No applications yet.</td>
                  </tr>
                ) : (
                  apps.map((a) => (
                    <tr key={a.id} className="border-b border-slate-100 align-top">
                      <td className="py-4 pr-4 font-medium text-slate-900">{a.full_name}</td>
                      <td className="py-4 pr-4 text-slate-700">
                        <div>{a.business_name}</div>
                        <div className="text-slate-500 text-[12px]">{a.business_type}</div>
                      </td>
                      <td className="py-4 pr-4"><a href={`mailto:${a.email}`} className="text-blue-600 hover:underline">{a.email}</a></td>
                      <td className="py-4 pr-4 text-slate-700">{a.situation}</td>
                      <td className="py-4 pr-4 text-slate-500">{new Date(a.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-project-modal">
            <div className="flex items-start justify-between mb-6">
              <h3 className="font-display text-2xl font-medium text-slate-900">{editing.id ? "Edit project" : "New project"}</h3>
              <button onClick={() => setEditing(null)}><X size={22} className="text-slate-500" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Business name *" value={editing.business_name} onChange={(v) => setEditing((s) => ({ ...s, business_name: v }))} testid="proj-name" />
              <Input label="Category" value={editing.category} onChange={(v) => setEditing((s) => ({ ...s, category: v }))} testid="proj-category" />
              <Input label="Image URL *" value={editing.image_url} onChange={(v) => setEditing((s) => ({ ...s, image_url: v }))} full testid="proj-image" />
              <Input label="Location" value={editing.location} onChange={(v) => setEditing((s) => ({ ...s, location: v }))} testid="proj-location" />
              <Input label="Live URL" value={editing.live_url} onChange={(v) => setEditing((s) => ({ ...s, live_url: v }))} testid="proj-live" />
              <div className="sm:col-span-2">
                <label className="text-[13px] font-medium text-slate-700">Description</label>
                <textarea
                  rows={4}
                  value={editing.description}
                  onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))}
                  className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-blue-600 focus:bg-white resize-none"
                  data-testid="proj-description"
                />
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="btn-outline-light">Cancel</button>
              <button onClick={saveProject} className="btn-primary" data-testid="admin-save-project-btn"><Save size={16} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, full, testid }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-[13px] font-medium text-slate-700">{label}</span>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-[15px] focus:outline-none focus:border-blue-600 focus:bg-white"
        data-testid={testid}
      />
    </label>
  );
}
